import { Docs } from "~/bff/types/Docs";
import { Variants } from "~/bff/types/Variants";
import { getProductCardIndexOnPage } from "~/components/product-card/helpers";
import { getDisplayPriceWithoutCurrency } from "~/helpers/get-display-price";
import { Config } from "~/utils/jsonLd";

import { JsonLdType, SchemaOffersUrl } from "../constants";

interface ProductItem {
  "@type": string;
  position?: number;
  url: string;
  name?: string;
  image?: string;
  description?: string;
  color?: string;
  sku?: string;
  size?: string[];
  brand?: Record<string, unknown>;
  productGroupID?: string;
  offers?: Record<string, unknown>;
  availableDeliveryMethod?: Record<string, unknown>;
}

interface ListItem {
  "@type": string;
  position: number | undefined;
  item: ProductItem;
}

export const getJsonLdForPlp = (
  products: Docs[],
  currencyCode: string,
  itemsMap: Map<string, number>,
  urlPrefix: string,
  brandName?: string,
): string => {
  const config = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.map((product) =>
      getProductItem(product, currencyCode, itemsMap, brandName, urlPrefix),
    ),
  };

  return JSON.stringify(config);
};
export const getJsonLdForSrp = (
  products: Docs[],
  currencyCode: string,
  itemsMap: Map<string, number>,
  urlPrefix: string,
  brandName: string,
): Config => {
  const config = {
    placeholders: {},
    overrides: {
      itemListElement: products.map((product) =>
        getProductItem(product, currencyCode, itemsMap, brandName, urlPrefix),
      ),
    },
  };

  return config;
};

const getProductItem = (
  product: Docs,
  currencyCode: string,
  itemsMap: Map<string, number>,
  brandName: string | undefined,
  urlPrefix: string,
): ListItem => {
  const firstVariant = product?.variants?.[0];
  const url = `${urlPrefix}/p/${product?.url}`;
  const productIndex = getProductCardIndexOnPage(itemsMap, product?.pid);
  const productItem: ProductItem = {
    "@type": JsonLdType.PRODUCT,
    name: product?.title ?? undefined,
    color: product?.sku_color ?? undefined,
    url,
    image: firstVariant?.productImages?.[0]?.url ?? undefined,
    sku: firstVariant?.skuid ?? undefined,
  };
  if (Array.isArray(product?.variants) && product?.variants?.length > 1) {
    getMultiVariantProduct(productItem, product, currencyCode);
  } else {
    getOneVariantProduct(productItem, product, currencyCode);
  }

  if (productItem.offers) {
    productItem.offers.seller = {
      "@type": "Organization",
      name: brandName,
    };
  }

  if (product.clickAndCollect || product.clickAndCollectExclusive) {
    if (productItem.offers) {
      productItem.offers.availableDeliveryMethod = SchemaOffersUrl.ON_SITE_PICK_UP;
    }
  }

  const listItem = {
    "@type": JsonLdType.LIST_ITEM,
    position: productIndex,
    item: productItem,
  };

  return listItem;
};

export const getOneVariantProduct = (
  productItem: ProductItem,
  product: Docs,
  currencyCode: string,
): void => {
  const firstVariant = product?.variants?.[0];
  const productPrice = firstVariant?.sku_price
    ? getDisplayPriceWithoutCurrency(firstVariant.sku_price.toString())
    : undefined;

  productItem.offers = {
    "@type": "Offer",
    priceCurrency: currencyCode,
    availability: SchemaOffersUrl.IN_STOCK,
  };

  if (productPrice) {
    productItem.offers.price = productPrice;
  }
};

export const getMultiVariantProduct = (
  productItem: ProductItem,
  product: Docs,
  currencyCode: string,
): void => {
  const variantsFiltered = Array.isArray(product?.variants)
    ? product?.variants.filter(
        (el): el is Variants => el !== null && el !== undefined,
      )
    : [];

  const [lowPrice, highPrice] = findMinAndMaxPrice(variantsFiltered);

  productItem.offers = {
    "@type": "AggregateOffer",
    priceCurrency: currencyCode,
    lowPrice,
    highPrice,
    offerCount: product?.variants?.length,
    availability: SchemaOffersUrl.IN_STOCK,
  };
};

export const findMinAndMaxPrice = (
  products: Variants[],
): [string | undefined, string | undefined] => {
  let minPrice: number | undefined;
  let maxPrice: number | undefined;

  for (const product of products) {
    if (minPrice === undefined || (product?.sku_price ?? 0) < minPrice) {
      minPrice = product?.sku_price ?? undefined;
    }

    if (maxPrice === undefined || (product?.sku_price ?? 0) > maxPrice) {
      maxPrice = product?.sku_price ?? undefined;
    }
  }

  const formattedMinPrice =
    minPrice !== null && minPrice !== undefined
      ? (minPrice / 100).toFixed(2)
      : undefined;
  const formattedMaxPrice =
    maxPrice !== null && maxPrice !== undefined
      ? (maxPrice / 100).toFixed(2)
      : undefined;

  return [formattedMinPrice, formattedMaxPrice];
};
